import type { OwnershipFlags } from '@risksmart-app/trpc/src/routers/frontend/shared/ownership-filter-input';

import { getQueryVariables } from '@/utils/myItemsFilterConditions';

/**
 * The dashboard's global filters as a register-hook argument (COI-691).
 *
 * Members are named identically to the matching tRPC register procedure input,
 * so a hook forwards by spread and the two cannot drift. Entities take the
 * subset they support via `Pick`.
 *
 * `from` / `to` are pre-resolved UTC ISO instants, INCLUSIVE at both ends.
 * Relative-range resolution and precision rounding stay client-side in the
 * widget (COI-601), hence no `precision` argument.
 */
export type DashboardFilterArgs<TDateField extends string> = {
  tagTypeIds?: string[];
  departmentTypeIds?: string[];
  dateRange?: { field: TDateField; from?: string; to?: string };
  ownership?: OwnershipFlags;
};

/**
 * An empty array means "no filter" — deliberately the OPPOSITE convention from
 * an all-false `ownership` object, which the server documents as "match
 * nothing". Do not fold the two together.
 *
 * Correctness is owned server-side: `dashboardFilterInput` applies the same
 * collapse, so `[]` cannot reach a repository as `{ in: [] }` however it is
 * sent. What this buys is cache-key hygiene, which cannot move to the server —
 * the react-query key is built from the raw input, so `{ tagTypeIds: [] }` and
 * `{}` would otherwise be two keys for one result set.
 */
export const normaliseArrayFilter = (ids?: string[]): string[] | undefined =>
  ids?.length ? ids : undefined;

/**
 * Returns `undefined` when every filter is unset, so a register page passing
 * none hashes to the same react-query key as the pre-COI-691 zero-argument
 * call — tRPC includes `input` in the key whenever it isn't literally
 * `undefined`, and `hashKey` keeps the `{input: {}}` wrapper even once every
 * member inside it is dropped. Only matters for a hook whose procedure took no
 * input at all before COI-691 (assessment, oversight).
 *
 * A hook that builds its input inline instead — several do, to merge in a
 * non-filter arg like `entityIds` — should call `normaliseArrayFilter` itself.
 * Forgetting it costs a duplicate cache entry, not a wrong result.
 */
export const toTrpcDashboardFilterInput = <TDateField extends string>(
  filters: DashboardFilterArgs<TDateField> &
    ResultHistoryWindowArg &
    FullResultHistoryArg
):
  | (DashboardFilterArgs<TDateField> &
      ResultHistoryWindowArg &
      FullResultHistoryArg)
  | undefined => {
  const tagTypeIds = normaliseArrayFilter(filters.tagTypeIds);
  const departmentTypeIds = normaliseArrayFilter(filters.departmentTypeIds);
  const { dateRange, ownership } = filters;
  // COI-837 — gate on the window having a BOUND, not on the object being
  // present, matching every other layer of this clip (`toResultHistoryBounds`,
  // `toRelationDateBounds`, `toResultHistoryWindowQueryParams`,
  // `toResultHistoryWindow`). A bound-less `{}` is not a filter: it clips
  // nothing downstream, so treating it as set would mint a second cache key for
  // one result set — the same hygiene `normaliseArrayFilter` buys for `[]`.
  const resultHistoryWindow =
    (filters.resultHistoryWindow?.from ?? filters.resultHistoryWindow?.to)
      ? filters.resultHistoryWindow
      : undefined;
  // COI-838 — `false` and absent both mean capped, so only `true` counts as
  // set; forwarding `false` would mint a second cache key for one result set.
  const fullResultHistory = filters.fullResultHistory ? true : undefined;

  if (
    tagTypeIds === undefined &&
    departmentTypeIds === undefined &&
    dateRange === undefined &&
    ownership === undefined &&
    // A window-only call must NOT collapse to `undefined`, or the clip is
    // dropped before it reaches `queryOptions`.
    resultHistoryWindow === undefined &&
    // A flag-only call must NOT collapse: it is the only argument a chart
    // sends with no date range, and dropping it truncates it to two points.
    fullResultHistory === undefined
  ) {
    return undefined;
  }

  const normalised: DashboardFilterArgs<TDateField> &
    ResultHistoryWindowArg &
    FullResultHistoryArg = {
    ...filters,
    tagTypeIds,
    departmentTypeIds,
  };

  // Drop a bound-less window rather than forwarding `{}`, so it hashes
  // identically to the same call with no window at all.
  if (normalised.resultHistoryWindow && !resultHistoryWindow) {
    delete normalised.resultHistoryWindow;
  }

  // Drop anything but `true` so it hashes identically to the flag being absent.
  // Checked directly rather than with the window's `normalised.x && !x` guard —
  // that only fires because a bound-less window is a truthy `{}`.
  if (!fullResultHistory) {
    delete normalised.fullResultHistory;
  }

  return normalised;
};

/** Hasura comparison bounds for a timestamptz column. */
export type DateBounds = { _gte?: string; _lte?: string };

export const toDateBounds = ({
  from,
  to,
}: {
  from?: string;
  to?: string;
}): DateBounds => ({ _gte: from, _lte: to });

/**
 * ANDs a base `bool_exp` with the predicates the dashboard filters contribute.
 *
 * Deliberately NOT a deep merge: both `useEntityWhereFilter` and the ownership
 * fragment can put an `_or` at the top level, and a deep merge concatenates
 * those arrays — turning "in my entity AND owned by me" into "… OR …". With no
 * extras present the base is returned untouched.
 */
export const composeWhere = <T extends object>(
  base: T | undefined,
  extras: (T | undefined)[]
): T | undefined => {
  const present = [base, ...extras].filter((e): e is T => !!e);

  if (present.length === 0) {
    return undefined;
  }

  if (present.length === 1) {
    return present[0];
  }

  return { _and: present } as T;
};

/**
 * COI-837 — the date window applied to a register hook's NESTED result history
 * (obligation / document / risk `assessmentResults`, indicator `results`).
 *
 * Intersected into the four register hooks' args types rather than folded into
 * `DashboardFilterArgs`: only 4 of the 17 registers carry a nested series, and
 * `dateRange` already means "narrow the ROWS" — a meaning policed by
 * `dashboardDatePredicates.ts`'s exhaustive per-entity `Record`. This is a clip
 * on a different selection, so it takes a distinct key and contributes no entry
 * to those maps.
 *
 * No `field` member: exactly one series column exists per entity (`TestDate`
 * for the three assessment-result registers, `ResultDate` for indicator), so
 * the column is fixed inside each hook.
 *
 * `from` / `to` are pre-resolved INCLUSIVE UTC ISO instants — same convention
 * as `DashboardFilterArgs`. The tRPC procedure input member is named
 * `resultHistoryWindow` too, so a hook forwards by spread and the two cannot
 * drift.
 */
export type ResultHistoryWindowArg = {
  resultHistoryWindow?: { from?: string; to?: string };
};

/**
 * The nested-series window as Hasura bounds, or `undefined` when there is
 * nothing to clip on.
 *
 * The `undefined` case is load-bearing twice over: it keeps a hook's GraphQL
 * variables byte-identical to the pre-COI-837 output when no window is
 * supplied, and it stops `{ TestDate: {} }` — a filter nobody asked for —
 * reaching the wire.
 */
export const toResultHistoryBounds = (window?: {
  from?: string;
  to?: string;
}): DateBounds | undefined =>
  window && (window.from ?? window.to) ? toDateBounds(window) : undefined;

/**
 * COI-837 — the nested-series clip as a GraphQL variables fragment, for the four
 * register hooks that carry one. Returns `{}` when there is nothing to clip on,
 * so the variable is OMITTED rather than sent as an explicit no-op — each of the
 * four documents already defaults it, and a register page passing no window must
 * produce variables byte-identical to the pre-COI-837 output.
 *
 * ALWAYS SUPPLY `TVariables` EXPLICITLY as the hook's generated
 * `GetXxxQueryVariables`. `graphqlVariables` is typed `Record<string, unknown>`
 * by the hook factory, so this type argument is the ONLY thing checking the
 * variable name and the fragment shape against the document — without it a typo
 * compiles clean and silently filters nothing. It cannot be inferred from
 * `build`'s return, which is why it is not optional in practice.
 */
export const toResultHistoryVariables = <TVariables>(
  window: { from?: string; to?: string } | undefined,
  build: (bounds: DateBounds) => Partial<TVariables>
): Partial<TVariables> => {
  const bounds = toResultHistoryBounds(window);

  return bounds ? build(bounds) : {};
};

/**
 * COI-838 — opts OUT of the newest-2 cap on a register hook's NESTED result
 * history. The default is capped: both register trend getters read index 0 and
 * index 1 and nothing else — and they are NOT one shared function, so changing
 * Policy's `calculateTrend` does not reach Compliance's own `getRatingTrend`.
 *
 * Only `DocumentRatingsOverTime` and `ComplianceRatingsOverTime` set it, via
 * `withFullResultHistory`. A separate add-on rather than a `DashboardFilterArgs`
 * member because it is not a dashboard filter — it is a constant property of
 * which widget is rendering. Named to match the tRPC input member, so a hook
 * forwards by spread and the two cannot drift.
 */
export type FullResultHistoryArg = { fullResultHistory?: boolean };

/**
 * COI-838 — the uncapped opt-in as a GraphQL variables fragment.
 *
 * Returns `{}` when unset so `$assessmentResultsLimit` is OMITTED, which is
 * load-bearing: per GraphQL variable coercion (§2.9.5) a default applies only
 * when the variable is ABSENT, never when sent as `null`. The charts need
 * `null` — Hasura's `limit` parser is nullable, so null and absent both mean
 * "no limit" — and omitting it is what applies the default of 2 to everyone
 * else.
 *
 * ALWAYS SUPPLY `TVariables` EXPLICITLY as the hook's generated
 * `GetXxxQueryVariables`: `graphqlVariables` is `Record<string, unknown>`, so
 * this is the only thing checking the variable name against the document —
 * without it a typo compiles clean and the charts silently stay capped at 2.
 */
export const toFullResultHistoryVariables = <TVariables>(
  fullResultHistory: boolean | undefined,
  build: () => Partial<TVariables>
): Partial<TVariables> => (fullResultHistory ? build() : {});

export type MyItemsFilterConditions = ReturnType<typeof getQueryVariables>;

/**
 * For the majority of registers, whose `tags` / `departments` relation sits
 * straight off the entity. One that traverses instead (consequence via its
 * issue, acceptance via `parents.risk`, test result via `parent`) supplies its
 * own lambda at the call site.
 */
export const tagsWhereOnEntity = (tagTypeIds: string[]) => ({
  tags: { TagTypeId: { _in: tagTypeIds } },
});

export const departmentsWhereOnEntity = (departmentTypeIds: string[]) => ({
  departments: { DepartmentTypeId: { _in: departmentTypeIds } },
});

/**
 * Requires a filter's builder exactly when `TFilters` declares that filter, and
 * forbids it (typed `never`) otherwise — so `Pick`ing a filter away from a
 * hook's args type is the only way to drop its builder. Without this, optional
 * builder params compile clean while silently dropping a filter the args type
 * still accepts (COI-691 review finding #2).
 */
type BuilderFor<
  TFilters,
  TFilterKey extends keyof DashboardFilterArgs<string>,
  TBuilderKey extends string,
  TBuilder,
> = TFilterKey extends keyof TFilters
  ? Record<TBuilderKey, TBuilder>
  : Partial<Record<TBuilderKey, never>>;

type DashboardWhereBuilders<TFilters, TWhere> = BuilderFor<
  TFilters,
  'tagTypeIds',
  'tagsWhere',
  (tagTypeIds: string[]) => TWhere
> &
  BuilderFor<
    TFilters,
    'departmentTypeIds',
    'departmentsWhere',
    (departmentTypeIds: string[]) => TWhere
  > &
  BuilderFor<
    TFilters,
    'ownership',
    'ownershipWhere',
    (conditions: MyItemsFilterConditions) => TWhere | undefined
  >;

/**
 * The date-field union a filters type carries.
 *
 * This is `TDateField`'s default rather than a plain `string` because supplying
 * ANY explicit type argument suppresses inference for the omitted trailing
 * ones, which then fall back to their default — so the five call sites that
 * spell out `TFilters` / `TWhere` would otherwise widen `dateRange.field` and
 * `datePredicates` to accept any string.
 */
type DateFieldOf<TFilters> =
  TFilters extends DashboardFilterArgs<infer TDateField> ? TDateField : never;

/**
 * Assembles the dashboard's four global filters into a single `where`,
 * ANDed onto the hook's own base predicate (COI-691).
 *
 * Extracted from the seventeen register hooks that each repeated it verbatim;
 * the date map and the traversal each filter needs stay at the call site.
 *
 * PASS THE HOOK'S OWN ARGS OBJECT AS `filters` — never a rebuilt literal.
 * `TFilters` infers from it and drives which of the three builders are required
 * vs. forbidden (see `BuilderFor` above). A literal checks the builders
 * against itself rather than the hook's public args, so dropping a filter from
 * it relaxes the matching builder to optional — leaving the GraphQL leg
 * silently not applying a filter the tRPC leg still does. Extra non-filter keys
 * (`entityIds`, `parentId`, `issueType`) are ignored and don't affect the
 * guard. A hook supporting fewer filters narrows its own args type instead —
 * see `useGetChangeRequests`.
 *
 * Type arguments infer from the arguments, except for a base-less hook, which
 * has no `base` to anchor `TWhere` — see the oversight register hook.
 *
 * `datePredicates` entries return a fragment rather than a bare `where`,
 * because some date fields ride an extra variable (the document register's
 * `filesWhere`, COI-696). The fragment's non-`where` keys come back as
 * `dateVariables` for the hook to spread; hooks without one ignore it.
 *
 * An `ownership` filter with no resolved `userId` fails closed inside
 * `getQueryVariables`. With no filters set the base is returned untouched.
 */
export const buildDashboardWhere = <
  TFilters extends DashboardFilterArgs<TDateField>,
  TWhere extends object,
  TDateFragment extends { where?: TWhere } = { where?: TWhere },
  TDateField extends string = DateFieldOf<TFilters>,
>({
  base,
  filters,
  userId,
  datePredicates,
  tagsWhere,
  departmentsWhere,
  ownershipWhere,
}: {
  /** The hook's own predicate — entity filter, parent scoping, and so on. */
  base?: TWhere;
  filters: TFilters;
  /** From `useRisksmartUser()` inside the hook; never passed by the caller. */
  userId?: string;
  /** The entity's map from `dashboardDatePredicates.ts`. */
  datePredicates: Record<TDateField, (bounds: DateBounds) => TDateFragment>;
} & DashboardWhereBuilders<TFilters, TWhere>): {
  where: TWhere | undefined;
  dateVariables: Omit<TDateFragment, 'where'>;
} => {
  const { tagTypeIds, departmentTypeIds, dateRange, ownership } = filters;

  const dateFragment = dateRange
    ? datePredicates[dateRange.field](toDateBounds(dateRange))
    : undefined;

  const where = composeWhere<TWhere>(base, [
    tagTypeIds?.length && tagsWhere ? tagsWhere(tagTypeIds) : undefined,
    departmentTypeIds?.length && departmentsWhere
      ? departmentsWhere(departmentTypeIds)
      : undefined,
    dateFragment?.where,
    ownership && ownershipWhere
      ? ownershipWhere(getQueryVariables(ownership, userId))
      : undefined,
  ]);

  // Every date map's non-`where` members are optional by convention, so `{}`
  // genuinely satisfies `Omit<TDateFragment, 'where'>` when there is no
  // fragment — TypeScript just can't see that through the generic.
  const { where: _dateWhere, ...dateVariables } =
    dateFragment ?? ({} as TDateFragment);

  return { where, dateVariables };
};
